<script setup lang="ts">
interface GalleryImage {
  src: string
  caption: string
}

interface Project {
  slug: string
  title: string
  heroImage?: string
  figmaUrl?: string
  presentationUrl?: string
  metadata: {
    timeframe: string
    role: string
    company: string
  }
  tags: string[]
  challenge: {
    problem: string
    beforeImage?: string
    stakeholders: string[]
    goals: string[]
  }
  process: {
    methods: string[]
    tools: string[]
    team: string
    duration: string
  }
  solution: {
    description: string
    keyFeatures: string[]
    designDecisions: string[]
    gallery?: GalleryImage[]
    wireframesGallery?: GalleryImage[]
    hifiGallery?: GalleryImage[]
  }
  impact: {
    kpis: string[]
    userFeedback?: string
    businessImpact: string
    lessonsLearned: string[]
  }
}

const projects: Project[] = [
  {
    slug: 'bauer-kirch-digitale-strategie',
    title: 'BAUER+KIRCH – Digitale Strategie & Online Bibliothek',
    presentationUrl: 'https://bit.ly/33CsLcS',
    metadata: {
      timeframe: '2020',
      role: 'UX Designer & Strategist',
      company: 'BAUER+KIRCH'
    },
    tags: ['UX Design', 'User Research'],
    challenge: {
      problem: 'Ein Weltmarktführer für Industrieanlagen (9 Standorte weltweit, 460+ Anlagen im Betrieb bis zu 15 Jahre) steht vor der digitalen Transformation. Kunden erhalten Betriebsanleitungen auf CD-ROM (700 Seiten), Techniker vor Ort haben oft keinen Zugriff. Ersatzteilbestellungen laufen nur per E-Mail, Telefon oder Fax. Budget 50 PT für Phase 1: digitale Strategie + MMP-Konzept als Bewerbungs-Case-Study für BAUER+KIRCH.',
      stakeholders: [
        'Techniker vor Ort an den Maschinen (Primärnutzer)',
        'Maschinenbetreiber / Produktionsleiter',
        'Service-Team des Anlagenbauers',
        'Product Owner / Management'
      ],
      goals: [
        'Digitale Strategie mit 10-Jahres-Roadmap erarbeiten',
        'Leuchtturmprojekt (MMP) konzipieren und visualisieren',
        'Kundenzufriedenheit steigern und Umsatz durch Digitalisierung erhöhen',
        'Neue Geschäftsmodelle erschließen'
      ]
    },
    process: {
      methods: [
        'Contextual Inquiry',
        'Online-Fragebogen',
        'Fokusgruppe',
        'Use Cases & Szenarien',
        'Customer Journey',
        'Design Thinking',
        'Ideation',
        'Rapid Prototyping (Wireframe)',
        'Remote Usability-Tests'
      ],
      tools: ['Figma'],
      team: 'Solo-Projekt (Bewerbungs-Case-Study bei BAUER+KIRCH)',
      duration: '2020 – im Rahmen eines Bewerbungsgesprächs'
    },
    solution: {
      description: 'Entwicklung einer digitalen Strategie mit 10-Jahres-Roadmap sowie Konzeption eines Minimal Marketable Product (MMP): die „Online Bibliothek" – eine mobile App, die alle Maschineninformationen digital zugänglich macht und direkte Wartungs- und Nachbestellfunktionen bietet.',
      keyFeatures: [
        '10-Jahres-Roadmap: 2020 Konzept → 2021 Leuchttürme → 2025 Digitale Maschinen → 2030 KI-Integration',
        'Online Bibliothek: alle Handbücher und technische Zeichnungen digital verfügbar',
        'QR-Code an der Maschine – direkter Sprung zur passenden Dokumentation',
        'Interaktive Zeichnungen mit Wartungshinweisen und Nachbestellfunktion',
        'Direkter Support-Kontakt aus der App heraus',
        'Shop-Integration für Ersatzteil-Nachbestellung'
      ],
      designDecisions: [
        'Mobile-First: Techniker arbeiten vor Ort mit Smartphones, nicht am Schreibtisch',
        'QR-Code als Entry-Point: eliminiert Suche und ermöglicht sofortigen Kontext zur richtigen Maschine',
        'Interaktive Zeichnungen statt PDF: kontextuelle Wartungshinweise direkt am Bauteil statt in 700-seitigem Handbuch',
        'Agiles Vorgehen mit iterativer Entwicklung: sichert schnelle Time-to-Market für das MMP'
      ],
      gallery: [
        {
          src: '/images/bauer-kirch/probekonzept-1.png',
          caption: 'Titelfolie des Probekonzepts – individuell erstellt für BAUER+KIRCH'
        },
        {
          src: '/images/bauer-kirch/probekonzept-2.png',
          caption: 'Digital Strategie – Vision: 10-Jahres-Roadmap von 2020 (Konzept) über 2021 (Leuchttürme) und 2025 (Digitale Maschinen) bis 2030 (KI-Integration)'
        },
        {
          src: '/images/bauer-kirch/online-bibliothek.png',
          caption: 'Online Bibliothek – Interaktionskonzept: vollständiger User Flow vom QR-Code-Scan bis zur Ersatzteil-Nachbestellung und Wartungsanfrage'
        }
      ]
    },
    impact: {
      kpis: [
        'Vollständige 10-Jahres-Digitalstrategie für einen Weltmarktführer',
        'MMP-Konzept: innerhalb von 3 Monaten entwickelbar (Budget 50 PT Phase 1)',
        'Vier konkrete Nutzerprobleme adressiert und in Features überführt',
        'Mobile Wireframes für alle zentralen User Flows der Online Bibliothek'
      ],
      businessImpact: 'Konzeptuelle Grundlage für die digitale Transformation eines Weltmarktführers für Industrieanlagen. Von CD-ROM zur vernetzten Service-Plattform – die Roadmap zeigt den Weg von 2020 bis 2030 und adressiert kurzfristige Quick-Wins ebenso wie langfristige KI-Integration.',
      lessonsLearned: [
        'B2B-Digitalisierung braucht einen klaren Leuchtturm – nicht alles auf einmal angehen',
        'Mobile-First gilt auch in der Industrie: Techniker arbeiten vor Ort, nicht am Desktop',
        'QR-Codes als Brücke zwischen physischer Maschine und digitaler Dokumentation sind intuitiv und wartungsarm',
        'Eine klare Zeitplanung (MMP in 3 Monaten) schafft Vertrauen beim Management'
      ]
    }
  },
  {
    slug: 'zep-zeiterfassung-redesign',
    title: 'ZEP Zeiterfassung – UX Redesign',
    metadata: {
      timeframe: '2023',
      role: 'UI/UX Designer',
      company: 'provantis IT Solutions'
    },
    figmaUrl: 'https://www.figma.com/design/HeJuACwQM6OVLwZtNQIj1r/Zeiterfassung?node-id=52-3412',
    tags: ['UX Design', 'User Research'],
    challenge: {
      beforeImage: '/images/zep/original-zeiterfassung.png',
      problem: 'Heuristische Analyse der bestehenden Zeiterfassungsmaske von ZEP (1.500+ Kunden, 50.000+ Nutzer täglich) im Rahmen einer Bewerbungs-Case-Study. Identifizierte Probleme: vollbreites Formular ermüdet Augen, falscher initialer Fokus (Bemerkungsfeld statt Datum), Scrollen bei vielen Buchungen.',
      stakeholders: [
        'Projektarbeiter (78 % der Nutzer, tägl. Zeiterfassung)',
        'Projektmanager (13 %, Projektübersicht & -planung)',
        'Buchhalter (9 %, monatliche Abrechnung)'
      ],
      goals: [
        'Zeiterfassungsmaske inkl. Navigation neu gestalten',
        'Drei Layoutkonzepte entwickeln und begründen',
        'Designprozess transparent dokumentieren'
      ]
    },
    process: {
      methods: [
        'Heuristische Evaluation',
        'User Research',
        'Nutzungsanalyse',
        'Informationsarchitektur',
        'Prototyping (Lo-Fi & Hi-Fi)'
      ],
      tools: ['Figma', 'CodePen'],
      team: 'Solo-Projekt (Bewerbungs-Case-Study bei provantis IT Solutions)',
      duration: '2023 – im Rahmen eines Bewerbungsgesprächs'
    },
    solution: {
      description: 'Entwicklung eines neuen Navigationskonzepts (vertikale Icon-Sidebar mit Flyout) kombiniert mit Master-Detail-Pattern für die Zeiterfassung. Drei Layoutvarianten wurden konzipiert und als Hi-Fi-Prototypen ausgearbeitet.',
      keyFeatures: [
        'Vertikale Icon-Navigation statt horizontaler Doppel-Menüleiste',
        'Flyout-Untermenü (dockbar als zweite Spalte)',
        'Master-Detail-Layout: Übersicht links, Eingabeformular rechts',
        'Mehrspaltige Projektansicht (je Projekt eine Spalte)',
        'Outlook-ähnliche Kalenderansicht mit farbkodierten Buchungen',
        'Warnhinweis bei Überbuchung pro Tag'
      ],
      designDecisions: [
        'Navigation nach links verlagert – Desktop nutzt horizontale Breite effizienter',
        'Formulare max. halbe Bildschirmbreite – reduziert Augenbewegung und Ermüdung',
        'Initialer Fokus korrigiert: erstes Datumsfeld statt Bemerkungsfeld',
        'Einfügen-Button je Tag erlaubt nachträgliche Buchungen ohne Umweg'
      ],
      // Bilder exportieren aus Figma: node-id 52-3412 und 64-9556 als PNG (2x)
      // → /website/public/images/zep/01-projektzeiten.png
      // → /website/public/images/zep/02-projektzeiten-4spaltig.png
      gallery: [
        {
          src: '/images/zep/01-projektzeiten.png',
          caption: 'Variante 1: Mehrspaltige Projektansicht mit vertikaler Icon-Sidebar und aufgeklapptem „Zeit erfassen"-Panel'
        },
        {
          src: '/images/zep/02-projektzeiten-4spaltig.png',
          caption: 'Variante 2: Vier-Spalten-Ansicht ohne Eingabe-Panel – maximale Übersicht für alle Projekte der Woche'
        }
      ]
    },
    impact: {
      kpis: [
        'Drei vollständige Redesign-Varianten mit unterschiedlichen Interaction-Patterns',
        'Hi-Fi-Prototyp in Figma mit funktionaler Navigation',
        'Konzept für 50.000+ tägliche Nutzer der ZEP-Plattform'
      ],
      businessImpact: 'Konzeptuelle Grundlage für eine modernisierte UX der ZEP-Zeiterfassungsplattform. Zeigt strukturierten UX-Prozess von Analyse über Research bis Prototyp.',
      lessonsLearned: [
        'Annahmen früh transparent machen – ermöglicht fundierte Designentscheidungen',
        'Mehrere Konzeptvarianten parallel zu entwickeln schafft bessere Entscheidungsgrundlage',
        'Bestehendes System erst validieren bevor neugestaltet wird'
      ]
    }
  },
  {
    slug: 'brz-365-design-system',
    title: 'BRZ 365 Design System',
    metadata: {
      timeframe: '2017 - 2019',
      role: 'Lead UX Designer',
      company: 'BRZ Deutschland GmbH'
    },
    tags: ['Design System', 'UX Design', 'Figma', 'Atomic Design'],
    challenge: {
      problem: 'BRZ hatte über 20 verschiedene Softwareprodukte im Portfolio, die alle unterschiedliche UI-Patterns und Designsprachen verwendeten. Dies führte zu Inkonsistenzen, längeren Entwicklungszeiten und einer schlechten User Experience über die Produktpalette hinweg.',
      stakeholders: [
        'Produktentwicklungs-Teams (20+ Entwickler)',
        'UX Designer-Team (4 Designer)',
        'Management und Produktverantwortliche',
        'End-User (Baufirmen und öffentliche Auftraggeber)'
      ],
      goals: [
        'Einheitliche Designsprache über alle BRZ-Produkte',
        'Reduzierung der Entwicklungszeit durch wiederverwendbare Komponenten',
        'Verbesserung der User Experience durch Konsistenz',
        'Skalierbare Design-Dokumentation für zukünftige Produkte'
      ]
    },
    process: {
      methods: [
        'UI Audit aller 20+ BRZ-Produkte',
        'Component Inventory Workshop mit Entwicklern',
        'Atomic Design Methodology',
        'Design Tokenization (Farben, Typografie, Spacing)',
        'User Testing mit Prototypen',
        'Iterative Design-Sprints'
      ],
      tools: ['Figma', 'Abstract', 'Zeroheight', 'Storybook'],
      team: '1 Lead UX Designer (ich), 2 UX Designer, 4 Frontend-Entwickler, 1 Product Owner',
      duration: '18 Monate (2017-2019)'
    },
    solution: {
      description: 'Entwicklung eines umfassenden Design Systems nach Atomic Design Prinzipien mit über 50 wiederverwendbaren Komponenten, vollständiger Dokumentation und Integration in die Entwicklungs-Pipeline.',
      keyFeatures: [
        '50+ UI-Komponenten (Buttons, Forms, Tables, Navigation, etc.)',
        'Design Tokens für Farben, Typografie, Spacing und Shadows',
        'Responsive Grid-System für alle Breakpoints',
        'Barrierefreie Komponenten (WCAG 2.1 Level AA)',
        'Figma Component Library mit Auto-Layout',
        'Zeroheight-Dokumentation für Entwickler',
        'Storybook-Integration für Code-Komponenten'
      ],
      designDecisions: [
        'Atomic Design: Aufbau von Atoms → Molecules → Organisms → Templates',
        'Component Variants: Flexible Komponenten mit Props statt vieler einzelner Components',
        'Design Tokens: Zentrale Definition aller Design-Werte für einfache Theme-Anpassung',
        'Mobile-First: Alle Komponenten zuerst für Mobile designed'
      ]
    },
    impact: {
      kpis: [
        '40% schnellere Entwicklungszeit für neue Features',
        '90% weniger Design-Inkonsistenzen über Produkte',
        '100% WCAG 2.1 Level AA Konformität',
        '15+ neue Produkte nutzen das Design System (Stand 2024)'
      ],
      businessImpact: 'Das BRZ 365 Design System wurde zum Standard für alle neuen BRZ-Produkte und ermöglichte es, schneller auf Marktanforderungen zu reagieren. Die Entwicklungskosten für neue Features wurden signifikant reduziert.',
      lessonsLearned: [
        'Design System ist nie "fertig" - kontinuierliche Pflege ist essentiell',
        'Enger Austausch mit Entwicklern von Anfang an spart später Zeit',
        'Dokumentation ist genauso wichtig wie das Design selbst',
        'Design Tokens ermöglichen flexible Theme-Anpassungen ohne Komponenten-Änderung'
      ]
    }
  },
  {
    slug: 'ok-pers-plus',
    title: 'OK.PERS+ Personalverwaltung',
    metadata: {
      timeframe: '2019 - 2021',
      role: 'UX Designer & User Researcher',
      company: 'ASTRUM IT GmbH'
    },
    tags: ['User Research', 'UX Design', 'Usability Testing', 'Enterprise Software'],
    challenge: {
      problem: 'OK.PERS+ ist eine komplexe Personalverwaltungssoftware für öffentliche Verwaltungen. User-Feedback zeigte hohe Fehlerquoten, lange Einarbeitungszeiten und Frustration bei alltäglichen Aufgaben. Die Software hatte über 15 Jahre Legacy-Code und eine veraltete Benutzeroberfläche.',
      stakeholders: [
        'Personalsachbearbeiter in öffentlichen Verwaltungen',
        'IT-Administratoren',
        'Produktmanagement ASTRUM IT',
        'Entwicklungsteam (10+ Entwickler)'
      ],
      goals: [
        'Reduzierung der Fehlerquote bei Dateneingaben',
        'Verkürzung der Einarbeitungszeit für neue Mitarbeiter',
        'Modernisierung der Benutzeroberfläche',
        'Verbesserung der User Satisfaction auf mindestens 4/5'
      ]
    },
    process: {
      methods: [
        'User Research: 5 Tiefeninterviews mit Personalsachbearbeitern',
        'Contextual Inquiry: Beobachtung der Arbeitsabläufe vor Ort',
        'User Journey Mapping: Identifikation von Pain Points',
        'Information Architecture: Neustrukturierung der Navigation',
        'Wireframing & Prototyping',
        'Usability Testing: 3 Iterationen mit 8 Testern'
      ],
      tools: ['Figma', 'Miro', 'Maze', 'UserTesting.com'],
      team: '1 UX Designer (ich), 1 User Researcher, 1 Product Owner, 5 Entwickler',
      duration: '14 Monate (2019-2021)'
    },
    solution: {
      description: 'Komplettes UX-Redesign mit Fokus auf vereinfachte Workflows, klarere Navigation und kontextuelle Hilfestellungen. Die neue Oberfläche reduziert kognitive Last und führt User intuitiv durch komplexe Prozesse.',
      keyFeatures: [
        'Neu strukturierte Navigation mit Task-basiertem Ansatz',
        'Schritt-für-Schritt-Wizards für komplexe Prozesse',
        'Inline-Validierung mit hilfreichen Fehlermeldungen',
        'Kontextuelle Hilfe-Tooltips',
        'Dashboard mit Quick-Actions für häufige Aufgaben',
        'Responsive Design für Tablet-Nutzung',
        'Durchgängige Suchfunktion'
      ],
      designDecisions: [
        'Task-basierte Navigation: User denken in Aufgaben, nicht in Modulen',
        'Progressive Disclosure: Komplexität nur zeigen, wenn nötig',
        'Feedback-First: Sofortige Rückmeldung bei allen Aktionen',
        'Error Prevention: Validierung vor Submit, nicht danach'
      ]
    },
    impact: {
      kpis: [
        '25% Reduktion der Fehlerquote bei Dateneingaben',
        '4.2/5 User Satisfaction Score (vorher 2.8/5)',
        '30% weniger Support-Anfragen',
        '40% schnellere Einarbeitung neuer Mitarbeiter'
      ],
      userFeedback: '"Die neue Oberfläche ist viel übersichtlicher. Ich finde jetzt alles sofort und mache weniger Fehler." - Personalsachbearbeiterin, Stadt München',
      businessImpact: 'Die UX-Optimierung führte zu signifikant höherer User-Zufriedenheit und weniger Support-Aufwand. OK.PERS+ konnte sich besser gegen Wettbewerber positionieren.',
      lessonsLearned: [
        'User Research ist essentiell - Annahmen sind oft falsch',
        'Kleine iterative Verbesserungen sind besser als ein "Big Bang Redesign"',
        'Enge Zusammenarbeit mit echten Usern während des gesamten Prozesses',
        'Legacy-Software braucht besondere Change-Management-Strategien'
      ]
    }
  },
  {
    slug: 'ergo-direkt',
    title: 'ERGO Direkt - Digitale Versicherungen',
    metadata: {
      timeframe: '2021 - 2023',
      role: 'Product Owner & UX Designer',
      company: 'ERGO Direkt AG'
    },
    tags: ['Product Owner', 'UX Design', 'A/B Testing', 'Conversion Optimization'],
    challenge: {
      problem: 'ERGO Direkt wollte den digitalen Abschluss von Versicherungen verbessern. Die bestehenden Online-Formulare hatten hohe Abbruchraten (über 60%) und niedrige Conversion Rates. User empfanden den Prozess als zu lang, kompliziert und nicht vertrauenswürdig.',
      stakeholders: [
        'Potenzielle Versicherungskunden',
        'Marketing-Team ERGO Direkt',
        'IT-Entwicklung',
        'Compliance & Legal',
        'Geschäftsführung'
      ],
      goals: [
        'Erhöhung der Conversion Rate um mindestens 10%',
        'Reduzierung der Abbruchrate im Funnel',
        'Verbesserung des Trust-Faktors',
        'Mobile-Optimierung (60% der User sind Mobile)'
      ]
    },
    process: {
      methods: [
        'Product Discovery: Stakeholder Interviews',
        'Analytics-Analyse: Identifikation von Drop-Off-Points',
        'Competitor Analysis: Best Practices im Versicherungsbereich',
        'User Testing: Funnel-Optimierung mit echten Usern',
        'A/B Testing: Datenbasierte Validierung von Design-Entscheidungen',
        'Agile Entwicklung: 2-Wochen-Sprints mit Scrum'
      ],
      tools: ['Figma', 'Hotjar', 'Google Analytics', 'Optimizely', 'Jira', 'Confluence'],
      team: 'Ich als Product Owner & UX Designer, 1 UI Designer, 6 Entwickler, 1 Scrum Master, 1 Data Analyst',
      duration: '18 Monate (2021-2023)'
    },
    solution: {
      description: 'Optimierter Versicherungs-Funnel mit klarem Schritt-für-Schritt-Prozess, reduzierten Formularfeldern, Trust-Elementen und Mobile-First-Design. Jede Änderung wurde A/B-getestet und datenbasiert validiert.',
      keyFeatures: [
        'Multi-Step-Formular mit Progress-Indicator',
        'Smart-Defaults basierend auf User-Profil',
        'Inline-Validierung mit hilfreichen Fehlermeldungen',
        'Trust-Badges und Sicherheits-Hinweise',
        'Live-Preis-Kalkulation',
        'Mobile-optimierte Eingabefelder',
        'Speichern & Später fortfahren Feature',
        'Chatbot für häufige Fragen während des Prozesses'
      ],
      designDecisions: [
        'One Thing Per Screen: Fokus auf eine Aufgabe pro Seite',
        'Progressive Profiling: Nur nötige Felder zeigen, Rest später',
        'Trust-First: Sicherheit und Datenschutz prominent kommunizieren',
        'Mobile-First: Design für Mobile, dann Desktop-Anpassung'
      ]
    },
    impact: {
      kpis: [
        '15% höhere Conversion Rate',
        '35% weniger Formular-Abbrüche',
        '45% mehr Mobile-Abschlüsse',
        '4.1/5 User Satisfaction Score'
      ],
      businessImpact: 'Die Conversion-Optimierung führte zu signifikant mehr Online-Abschlüssen und reduzierte die Kosten pro Akquisition. ERGO Direkt konnte die digitale Transformation beschleunigen und sich als moderner Versicherer positionieren.',
      lessonsLearned: [
        'A/B Testing ist essentiell - Bauchgefühl kann täuschen',
        'Mobile-First ist kein Buzzword - 60%+ User sind Mobile',
        'Trust ist im Versicherungsbereich der wichtigste Faktor',
        'Kleine Änderungen können große Wirkung haben (z.B. Button-Text)',
        'Balance zwischen Compliance-Anforderungen und UX ist eine Herausforderung'
      ]
    }
  },
  {
    slug: 'fiege-mitarbeitervernetzung',
    title: 'FIEGE myFIEGE – Mitarbeitervernetzung',
    metadata: {
      timeframe: '2023',
      role: 'UX Designer',
      company: 'FIEGE Logistik'
    },
    tags: ['UX Design', 'User Research'],
    challenge: {
      problem: 'Assessment Center Aufgabe bei FIEGE Logistik: Wie kann die digitale Kollaborations-Plattform „myFIEGE" genutzt werden, um die Vernetzung von Mitarbeitenden zu verbessern? In Zeiten digitaler Arbeit und Standortunabhängigkeit finden viele Mitarbeitende es schwierig, auf Vorgesetzte, neue Kolleg:innen oder auf die aus anderen Bereichen zuzugehen. Ob Synergien erkannt werden, Hierarchien überwunden und Mitarbeitende gerne ins Büro kommen, hängt stark davon ab, wie vernetzt sie untereinander sind.',
      stakeholders: [
        'Mitarbeitende aller FIEGE-Standorte (Primärnutzer)',
        'People & Culture Team (interne Auftraggeber)',
        'Führungskräfte und Abteilungsleiter',
        'myFIEGE Produktteam'
      ],
      goals: [
        'Barrieren für mangelnde Mitarbeitervernetzung identifizieren',
        'Kreatives Feature-Konzept für myFIEGE entwickeln',
        'Interface-Skizze des Features erstellen',
        'Forschungsmethodik und iteratives Vorgehen aufzeigen'
      ]
    },
    process: {
      methods: [
        'Barrier-Analyse: 4 Vernetzungsbarrieren identifiziert (Zeitmangel, Transparenz, fehlendes Interesse, Kommunikationshemmungen)',
        'Mitarbeiterbefragungen (Forschungsdesign für gezielte Datenerhebung)',
        'Fokusgruppen zur Analyse von Motivationsmustern',
        'Datenanalyse bestehender Kollaborations- und Kommunikationstools',
        'Competitive Research: Microsoft Viva Insights, LinkedIn, Google Local Guides als Referenz',
        '7-stufige Implementierungs-Roadmap entwickelt'
      ],
      tools: ['Figma', 'Keynote'],
      team: 'Solo-Projekt (Assessment Center Aufgabe bei FIEGE Logistik)',
      duration: '2023 – 120 Minuten Bearbeitungszeit + 15 Minuten Präsentation'
    },
    solution: {
      description: 'Entwicklung eines Vernetzungs-Features für myFIEGE, das gezielt die vier identifizierten Barrieren adressiert. Der Lösungsansatz kombiniert ein Gamification-System (Punkte, Rankings, Auszeichnungen) mit smarten Vernetzungsvorschlägen basierend auf gemeinsamen Projekten, Interessen und Meeting-Kontakten – inspiriert von Microsoft Viva Insights und LinkedIn.',
      keyFeatures: [
        'Gamification: Punktesystem ähnlich Google Local Guides – Feedback geben und Wissen teilen wird belohnt',
        'Ranglisten mit sozialer Sichtbarkeit – Top-Platzierungen werden regelmäßig gewürdigt',
        'Smarte Vernetzungsvorschläge: Tool macht Vorschläge zu Kolleg:innen mit ähnlichen Aufgaben und Interessen',
        'Feedback-Integration: Tool holt aktiv Lob-Vorschläge aus Meetings, E-Mails und Chats (wie Microsoft Viva Insights)',
        'Micro-Interaction „Lob senden": Vernetzung in Sekunden – niedrigschwellig und emotional positiv',
        'Wissensverbreitung aktiv fördern: Teilen von Inhalten als Vernetzungsanlass'
      ],
      designDecisions: [
        'Low Effort First: Vernetzung muss in wenigen Sekunden möglich sein – kein Meeting-Overhead',
        'Wert sofort kommunizieren: Jeder Vorschlag zeigt den persönlichen Nutzen der Verbindung',
        'Positive Verstärkung statt Pflicht: Gamification und Lob-Features machen Vernetzung angenehm, nicht verpflichtend',
        'Kontext-getriggert: Vorschläge entstehen aus echten Interaktionen (Meetings, Projekte) – kein kalter Outreach'
      ],
      gallery: [
        {
          src: '/images/fiege/ac-praesentation-1.png',
          caption: 'Gamification-Konzept: Mitarbeitende sammeln Punkte durch Feedback und Interaktion – Top-Platzierungen werden für alle sichtbar gewürdigt'
        },
        {
          src: '/images/fiege/ac-praesentation-2.png',
          caption: 'Referenz Microsoft Viva Insights: Proaktive „Lob senden"-Feature als Inspiration für niedrigschwellige Vernetzung'
        },
        {
          src: '/images/fiege/ac-praesentation-3.png',
          caption: 'Maßnahmen zur Motivationssteigerung: Punktesystem, smarte Vernetzungsvorschläge und kontextbasierte Feedback-Integration'
        }
      ]
    },
    impact: {
      kpis: [
        'Assessment Center erfolgreich absolviert',
        '4 Vernetzungsbarrieren systematisch identifiziert und adressiert',
        '7-stufige Implementierungs-Roadmap mit Pilotphase und Messgrößen',
        'Feature-Konzept inklusive Interface in 120 Minuten entwickelt und präsentiert'
      ],
      businessImpact: 'Das Konzept zeigt einen vollständigen UX-Prozess von der Nutzerforschung über die Barrier-Analyse bis zum konkreten Feature-Design mit Interface-Visualisierung. Der iterative Ansatz mit Pilotphase, kontinuierlichem Feedback und klaren Erfolgsmessgrößen ermöglicht eine datenbasierte Weiterentwicklung der myFIEGE-Plattform.',
      lessonsLearned: [
        'Employee Engagement beginnt mit Empathie: erst Barrieren verstehen, dann Lösungen bauen',
        'Gamification funktioniert nur, wenn der Grundnutzen stimmt – Spielmechanik kommt nach dem Mehrwert',
        'Niedrigschwelligkeit ist entscheidend: Je weniger Aufwand, desto höher die Adoption',
        'Kontext schlägt Kaltakquise: Vernetzungsvorschläge aus echten Interaktionen werden besser angenommen'
      ]
    }
  },
  {
    slug: 'debeka-ux-berater-probeaufgaben',
    title: 'Debeka – UX-Berater Probeaufgaben',
    metadata: {
      timeframe: '2020',
      role: 'UX-Berater (Bewerbung)',
      company: 'Debeka'
    },
    tags: ['UX Design', 'User Research'],
    challenge: {
      problem: 'Probeaufgabe im Rahmen einer Bewerbung als UX-Berater bei der Debeka (September 2020, 7 Arbeitstage). Vier Aufgaben decken die gesamte Bandbreite eines UX-Beraters ab: theoretisches Fundament, Anforderungsanalyse auf Basis eines Kontextinterviews, Expertenevaluation mit Redesign-Konzept sowie praxisnahe Beratungsszenarien.',
      stakeholders: [
        'Service-Center-Mitarbeiter der Debeka (primäre Nutzer der evaluierten Anwendungen)',
        'Kunden von Debeka.de (Nutzer des AR-Tarifrechners)',
        'UX-Abteilung Debeka (interne Auftraggeber der Probeaufgabe)'
      ],
      goals: [
        'Theoretisches UX-Wissen unter Beweis stellen (Usability vs. UX, Nutzungskontext, Aufgabenmodell, Evaluation)',
        '22+ Nutzungsanforderungen aus einem Kontextinterview ableiten und strukturieren',
        'Usability-Probleme im Debeka AR-Tarifrechner mittels Expertenevaluation identifizieren',
        'Konkrete Redesign-Konzepte für gefundene Probleme entwickeln',
        'Praxisszenarien aus dem Alltag eines UX-Beraters lösen'
      ]
    },
    process: {
      methods: [
        'UX-Theorie: Ausarbeitung von Usability vs. UX, Nutzungskontext, Benutzereigenschaften, Aufgabenmodell, Gruppierung, heuristische Evaluation',
        'Anforderungsanalyse: Auswertung eines Kontextinterviews mit Service-Center-Mitarbeitern – 22+ Nutzungsanforderungen in 4 Kategorien (Anwendungsunterstützung, Arbeitsunterstützung, Wissensvermittlung, sonstige Anforderungen)',
        'Expertenevaluation: Inspektion des AR-Versicherungsrechners auf Debeka.de nach Nielsen\'schen Heuristiken',
        'Redesign-Konzepte: Lo-Fi-Skizzen und Wireframe-Konzepte für alle identifizierten Probleme',
        'UX-Beratungsszenarien: Praxisfälle aus dem Alltag eines UX-Beraters'
      ],
      tools: ['Keynote', 'Figma'],
      team: 'Solo-Projekt (Bewerbungs-Probeaufgabe bei Debeka)',
      duration: '2020 – 7 Arbeitstage (Abgabefrist 17.09.2020)'
    },
    solution: {
      description: 'Vollständige 49-seitige Präsentation, die alle vier Aufgaben beantwortet: UX-Theorie mit eigenen Definitionen und Beispielen, strukturierte Anforderungsanalyse mit priorisierten Nutzungsanforderungen, Expertenevaluation des AR-Tarifrechners mit drei identifizierten Usability-Problemen (Wizard-Probleme, Leseschwierigkeiten, verdeckte Sub-Navigation) sowie konkrete Redesign-Konzepte für jedes gefundene Problem.',
      keyFeatures: [
        'Anforderungsanalyse: 22+ Nutzungsanforderungen aus Kontextinterview in 4 Kategorien',
        'Expertenevaluation Wizard: Zu volle Browserbreite → Lesebereichsoptimierung, unnötige Felder → Reduktion auf Wesentliches, kryptische Schritt-Benennung → nutzerfreundliche Labels',
        'Expertenevaluation Leseschwierigkeit: Inkonsistente Typografie und Farbgestaltung im Formular – Analyse mit Redesign-Vorschlag',
        'Expertenevaluation Sub-Navigation: Verdeckte Navigation für eingeloggten Bereich',
        'UX-Beratungsszenarien: Lösung praxisnaher Fälle aus dem UX-Consulting-Alltag',
        'Durchgängige Begründung aller Designentscheidungen mit UX-Prinzipien'
      ],
      designDecisions: [
        'Heuristische Evaluation nach Nielsen: Systematische Inspektion statt subjektiver Kritik',
        'Lesebereich statt Vollbreite: Formulare auf optimale Zeilenlänge (60–80 Zeichen) begrenzen',
        'Progressive Disclosure im Wizard: Nur relevante Felder je Schritt anzeigen, Komplexität schrittweise aufbauen',
        'Konsistente Typografie-Hierarchie: Klare Unterscheidung von Labels, Werten und Hilfstexten'
      ],
      gallery: [
        {
          src: '/images/debeka/analyse-1.png',
          caption: 'Expertenevaluation – Aufgabenstellung: Analyse des AR-Versicherungsrechners auf Debeka.de mit identifizierten Usability-Problemen'
        },
        {
          src: '/images/debeka/analyse-2.png',
          caption: 'Wizard-Optimierung: Drei Lösungsansätze – optimale Lesebreite statt voller Browserbreite, Reduktion auf relevante Felder, nutzerfreundliche Schritt-Benennung'
        },
        {
          src: '/images/debeka/analyse-3.png',
          caption: 'Leseschwierigkeit: Analyse der typografischen und farblichen Inkonsistenzen im Formular mit konkretem Redesign-Konzept'
        }
      ]
    },
    impact: {
      kpis: [
        'Vollständige 49-seitige Präsentation zu allen 4 Aufgabenstellungen',
        '22+ Nutzungsanforderungen systematisch aus Kontextinterview abgeleitet',
        '3 Usability-Probleme im AR-Tarifrechner identifiziert und mit Redesign-Konzepten gelöst',
        'Probeaufgabe in 7 Arbeitstagen vollständig abgeliefert'
      ],
      businessImpact: 'Die Probeaufgabe demonstriert den vollständigen UX-Methodenkoffer: von der theoretischen Fundierung über strukturierte Anforderungsanalyse und systematische Expertenevaluation bis zu konkreten Designlösungen. Zeigt die Fähigkeit, schnell in unbekannte Domänen einzusteigen und praxisrelevante Ergebnisse zu liefern.',
      lessonsLearned: [
        'Expertenevaluationen brauchen eine strukturierte Methodik – Heuristiken verhindern subjektive Kritik',
        'Anforderungsanalyse aus Interviews: Zuhören reicht nicht – Nutzungsanforderungen müssen explizit abgeleitet und kategorisiert werden',
        'Wizard-Designs scheitern oft an zu viel Komplexität pro Schritt – weniger ist mehr',
        'Typografische Konsistenz ist kein Detail: Sie ist entscheidend für kognitive Belastung und Vertrauen'
      ]
    }
  },
  {
    slug: 'anwalt-de-snack-kiosk',
    title: 'Anwalt.de – SnackEase App Konzept',
    figmaUrl: 'https://www.figma.com/proto/s06TZ9Wd5fuhD4mlWC9Ihe/Case-Study-%E2%80%93-Senior-Product-Designer-Enrico--Copy-?page-id=246%3A167&node-id=246-793&scaling=min-zoom&content-scaling=fixed&starting-point-node-id=283%3A1849',
    metadata: {
      timeframe: '2023',
      role: 'UX Designer (Assessment)',
      company: 'Anwalt.de'
    },
    tags: ['UX Design', 'User Research'],
    challenge: {
      problem: 'Bewerbungs-Assessment bei Anwalt.de: Konzeption einer mobilen App für einen Mitarbeiter-Snack-Kiosk an den Standorten Nürnberg und Berlin. Mitarbeiter erhalten 25 € monatliches Guthaben und können Snacks (Obst, Proteinriegel, Shakes, Schokoriegel, Nüsse) auf Vertrauensbasis selbst buchen – mit nur einer Berührung. Zusätzlich soll ein Leaderboard Gesundheitsbewusstsein und Wir-Gefühl fördern.',
      stakeholders: [
        'Mitarbeiter von Anwalt.de (Primärnutzer: Nürnberg & Berlin)',
        'App-Administratoren und Finanzabteilung (Guthabenverwaltung)',
        'Management von Anwalt.de (Motivation, Mitarbeiterbindung)',
        'Lieferanten von Snacks und Getränken'
      ],
      goals: [
        'Snack-Buchung mit einer einzigen Berührung (1-Touch-Prinzip)',
        'Persönliche Statistiken: Guthaben, Ausgaben, Kaufhistorie, Nährwerte auf einen Blick',
        'Leaderboard mit Bonuspunkten für gesunde Produkte zur spielerischen Motivation',
        'Gesunde Ernährung fördern und Anreize für regelmäßige Büropräsenz schaffen',
        'Hi-Fi-Wireframes für alle Kernflows liefern'
      ]
    },
    process: {
      methods: [
        'Stakeholder-Analyse: 9 Nutzergruppen identifiziert (Mitarbeiter, Admins, Ernährungsberater, Entwickler, Finanzabteilung, Kundensupport, Management, Lieferanten, Selbstbedienungsnutzer)',
        'Nutzergruppen-Segmentierung: Hauptgruppe in 9 Untergruppen (Neue Mitarbeiter, Belegschaft, Gesundheitsbewusste, Gelegenheitsnutzer, Botschafter, Entdecker, Zeitsensible, Technikaffine, Gruppenbesteller)',
        'Persona-Entwicklung: 3 repräsentative Personas (Lucas Gesundheitsfan, Maxine Snackliebhaber, Nina Neuanfang)',
        'Aufgabenmodell: 11 Kernaufgaben der App definiert',
        'Screenflow / Informationsarchitektur: 14 Screens strukturiert und verknüpft',
        'Hi-Fi Wireframe-Konzept: "SnackEase – Dein Weg zu Gesundheit und Genuss"'
      ],
      tools: ['Figma', 'Keynote'],
      team: 'Solo-Projekt (Bewerbungs-Assessment bei Anwalt.de)',
      duration: '2023 – im Rahmen eines Bewerbungsgesprächs'
    },
    solution: {
      description: 'Vollständiger Hi-Fi-Prototyp "Snack Ease" in Figma mit 19 verknüpften Screens und klickbarem User Flow – von Splashscreen und Login über Produktübersicht, Suche, Kauf-Flow mit Scan & Bezahlen bis zu Bestenliste, Profil, Statistiken und Kaufhistorie. Design-System auf Basis von Mulish-Typografie und einem blauen Primary-System (#2F80ED), Tab-Navigation mit 5 Tabs: Snacks, Suche, Kaufen, Best, Profil.',
      keyFeatures: [
        'Splashscreen & Onboarding: "Willkommen bei Snack Ease" mit illustriertem Food-Character, Login & Registrierung',
        'Produktübersicht: Guthaben-Anzeige (18,50 €) mit Progress Bar, horizontale Produkt-Cards (Angebote), Empfehlungen/Favoriten-Filter',
        'Produktkarte: Produktbild, Name, Punkte, Preis, 1-Touch-Kauf-Button direkt in der Card',
        'Kauf-Flow: Produktdetail → Scan → Bezahlen → Kaufbeleg – komplett in 3 Schritten',
        'Bestenliste (Leaderboard): Top-3-Podium mit Gold/Silber/Bronze-Medals + komplette Rangliste ab Platz 4',
        'Profil & Statistiken: Persönliches Dashboard mit Kaufhistorie, Nährwert-Zusammenfassung, Account-Verwaltung',
        '19 Screens: vollständig verlinkt als klickbarer Figma-Prototyp'
      ],
      designDecisions: [
        '1-Touch-Prinzip: Kauf-Button direkt in der Produktkarte – kein Umweg über Detailseite nötig',
        'Tab-Bar mit 5 Tabs (Snacks/Suche/Kaufen/Best/Profil): klare Trennung der Hauptfunktionen, immer sichtbar',
        'Leaderboard "Bestenliste" mit Podium-Visualisierung: sozialer Vergleich macht Gesundheitspunkte sichtbar und motivierend',
        'Progress Bar für Guthaben: schnelle visuelle Rückmeldung über verbleibendes Budget ohne Rechnen'
      ],
      gallery: [
        {
          src: '/images/anwalt-de/screenflow.png',
          caption: 'Informationsarchitektur / Screenflow: 19 Screens von Splashscreen und Anmeldung über Produktkatalog, Kauf-Flow und Bestenliste bis zu Profil, Statistiken und Kaufhistorie'
        }
      ],
      wireframesGallery: [
        { src: '/images/anwalt-de/wireframes/start.png', caption: 'Start: Snacks-Übersicht mit Guthaben-Anzeige und Produktempfehlungen' },
        { src: '/images/anwalt-de/wireframes/auswahl.png', caption: 'Auswahl: Produktkatalog mit Kategorie-Filter und 1-Touch-Kauf-Button' },
        { src: '/images/anwalt-de/wireframes/suche.png', caption: 'Suche: Volltextsuche mit gefilterter Produktliste' },
        { src: '/images/anwalt-de/wireframes/kaufen.png', caption: 'Kaufen: Kauf-Flow – Scan & Bezahlen in 3 Schritten' },
        { src: '/images/anwalt-de/wireframes/leaderboard.png', caption: 'Bestenliste: Leaderboard mit Gesundheitspunkten und Rangliste' },
        { src: '/images/anwalt-de/wireframes/profil.png', caption: 'Profil: Persönliche Statistiken, Nährwerte und Kaufhistorie' },
        { src: '/images/anwalt-de/wireframes/frame-01.png', caption: 'Wireframe Frame 1' },
        { src: '/images/anwalt-de/wireframes/frame-02.png', caption: 'Wireframe Frame 2' },
        { src: '/images/anwalt-de/wireframes/frame-03.png', caption: 'Wireframe Frame 3' },
        { src: '/images/anwalt-de/wireframes/frame-04.png', caption: 'Wireframe Frame 4' },
        { src: '/images/anwalt-de/wireframes/frame-05.png', caption: 'Wireframe Frame 5' },
        { src: '/images/anwalt-de/wireframes/frame-06.png', caption: 'Wireframe Frame 6' },
        { src: '/images/anwalt-de/wireframes/frame-07.png', caption: 'Wireframe Frame 7' },
        { src: '/images/anwalt-de/wireframes/frame-08.png', caption: 'Wireframe Frame 8' },
        { src: '/images/anwalt-de/wireframes/frame-09.png', caption: 'Wireframe Frame 9' },
        { src: '/images/anwalt-de/wireframes/frame-10.png', caption: 'Wireframe Frame 10' },
        { src: '/images/anwalt-de/wireframes/frame-11.png', caption: 'Wireframe Frame 11' },
        { src: '/images/anwalt-de/wireframes/frame-12.png', caption: 'Wireframe Frame 12' },
        { src: '/images/anwalt-de/wireframes/frame-13.png', caption: 'Wireframe Frame 13' },
        { src: '/images/anwalt-de/wireframes/frame-14.png', caption: 'Wireframe Frame 14' },
        { src: '/images/anwalt-de/wireframes/frame-15.png', caption: 'Wireframe Frame 15' },
        { src: '/images/anwalt-de/wireframes/frame-16.png', caption: 'Wireframe Frame 16' }
      ],
      hifiGallery: [
        { src: '/images/anwalt-de/hifi/splashscreen.png', caption: 'Splashscreen: "Willkommen bei Snack Ease" mit illustriertem Food-Character' },
        { src: '/images/anwalt-de/hifi/login.png', caption: 'Login: Anmeldebildschirm mit E-Mail und Passwort' },
        { src: '/images/anwalt-de/hifi/login-1.png', caption: 'Login (Variante): Onboarding-Flow für neue Mitarbeiter' },
        { src: '/images/anwalt-de/hifi/product-overview.png', caption: 'Produktübersicht: Guthaben-Progress-Bar, Angebote und Empfehlungen' },
        { src: '/images/anwalt-de/hifi/product-page.png', caption: 'Produktdetail: Produktbild, Nährwerte, Punkte und 1-Touch-Kauf-Button' },
        { src: '/images/anwalt-de/hifi/search.png', caption: 'Suche: Produktsuche mit Echtzeit-Ergebnissen und Filter' },
        { src: '/images/anwalt-de/hifi/buy.png', caption: 'Kaufen: Übersicht ausgewählter Produkte vor dem Bezahlen' },
        { src: '/images/anwalt-de/hifi/product-order.png', caption: 'Produktbestellung: Scan-Screen für den Kiosk-Barcode' },
        { src: '/images/anwalt-de/hifi/payment.png', caption: 'Zahlung: Zahlungsbestätigung mit Guthabenabzug' },
        { src: '/images/anwalt-de/hifi/credit.png', caption: 'Guthaben: Monatliches Budget-Übersicht (25 €) mit Verbrauchsanzeige' },
        { src: '/images/anwalt-de/hifi/proof-of-purchase.png', caption: 'Kaufbeleg: Detaillierter Beleg nach erfolgreichem Kauf' },
        { src: '/images/anwalt-de/hifi/leaderboard.png', caption: 'Bestenliste: Top-3-Podium mit Gold/Silber/Bronze + Rangliste ab Platz 4' },
        { src: '/images/anwalt-de/hifi/profil.png', caption: 'Profil: Dashboard mit Statistiken, Nährwert-Zusammenfassung und Einstellungen' },
        { src: '/images/anwalt-de/hifi/user.png', caption: 'Benutzerprofil: Account-Verwaltung und persönliche Daten' }
      ]
    },
    impact: {
      kpis: [
        'Vollständige UX-Dokumentation: Stakeholder-Analyse, 3 Personas, Aufgabenmodell, Nutzungskontext',
        'Screenflow mit 14 verknüpften App-Screens',
        'Hi-Fi Wireframe-Konzept "SnackEase" abgeliefert',
        'Assessment erfolgreich absolviert'
      ],
      businessImpact: 'Das SnackEase-Konzept zeigt einen vollständigen User-Centered-Design-Prozess für eine Consumer-App im B2E-Kontext (Business-to-Employee): von der Zielgruppenanalyse über Personas und Aufgabenmodell bis zum konkreten Screenflow und Hi-Fi-Wireframe. Besondere Stärke: das Leaderboard verknüpft Mitarbeitermotivation, Gesundheitsförderung und Wir-Gefühl in einer einzigen Funktion.',
      lessonsLearned: [
        '1-Touch ist ein Design-Prinzip, kein Feature: die gesamte Informationsarchitektur muss darauf ausgerichtet sein',
        'Vertrauenssysteme schaffen Motivation – externe Überprüfungen erzeugen Reibung und signalisieren Misstrauen',
        'Gamification im B2E-Kontext: Leaderboards funktionieren nur, wenn das Grundangebot (Produkte, Preise, UX) stimmt',
        'Personas müssen Nutzungskontext widerspiegeln: zeitsensible Nutzer brauchen andere Flows als gesundheitsbewusste'
      ]
    }
  }
]

const route = useRoute()
const slug = route.params.slug as string

// Find current project
const project = projects.find(p => p.slug === slug)

// If project not found, redirect to portfolio
if (!project) {
  navigateTo('/portfolio')
}

// Find prev/next projects
const currentIndex = projects.findIndex(p => p.slug === slug)
const prevProject = currentIndex > 0 ? projects[currentIndex - 1] : null
const nextProject = currentIndex < projects.length - 1 ? projects[currentIndex + 1] : null

// SEO
useHead({
  title: `${project?.title} - Case Study - Enrico Reinsdorf`,
  meta: [
    {
      name: 'description',
      content: project?.challenge.problem.substring(0, 155) || ''
    }
  ]
})
</script>

<template>
  <div v-if="project" class="min-h-screen bg-white">
    <!-- Back Button -->
    <div class="bg-gray-50 border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-6 py-4">
        <NuxtLink
          to="/portfolio"
          class="inline-flex items-center text-dark-600 hover:text-primary-500 transition-colors"
        >
          <svg
            class="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Zurück zur Portfolio-Übersicht
        </NuxtLink>
      </div>
    </div>

    <!-- Hero Image -->
    <div class="w-full h-96 bg-gray-200 flex items-center justify-center overflow-hidden">
      <img
        v-if="project.solution.gallery?.[0]"
        :src="project.solution.gallery[0].src"
        :alt="project.title"
        class="w-full h-full object-cover object-top"
      />
      <p v-else class="text-gray-400 text-lg">Hero-Bild Platzhalter</p>
    </div>

    <!-- Content Container -->
    <div class="max-w-4xl mx-auto px-6 py-16">
      <!-- Project Header -->
      <header class="mb-16">
        <h1 class="text-5xl font-bold text-dark-900 mb-6">
          {{ project.title }}
        </h1>

        <!-- Metadata -->
        <div class="flex flex-wrap gap-6 text-dark-600 mb-6">
          <div class="flex items-center">
            <svg
              class="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {{ project.metadata.timeframe }}
          </div>
          <div class="flex items-center">
            <svg
              class="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            {{ project.metadata.role }}
          </div>
          <div class="flex items-center">
            <svg
              class="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            {{ project.metadata.company }}
          </div>
        </div>

        <!-- Tags -->
        <div class="flex flex-wrap gap-2 mb-6">
          <span
            v-for="tag in project.tags"
            :key="tag"
            class="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full"
          >
            {{ tag }}
          </span>
        </div>

        <!-- Figma Button -->
        <a
          v-if="project.figmaUrl"
          :href="project.figmaUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-2 px-5 py-2.5 bg-dark-900 text-white text-sm font-medium rounded-lg hover:bg-dark-700 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 38 57" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 28.5C19 25.9804 20.0009 23.5641 21.7825 21.7825C23.5641 20.0009 25.9804 19 28.5 19C31.0196 19 33.4359 20.0009 35.2175 21.7825C36.9991 23.5641 38 25.9804 38 28.5C38 31.0196 36.9991 33.4359 35.2175 35.2175C33.4359 36.9991 31.0196 38 28.5 38C25.9804 38 23.5641 36.9991 21.7825 35.2175C20.0009 33.4359 19 31.0196 19 28.5Z" fill="#1ABCFE"/>
            <path d="M0 47.5C0 44.9804 1.00089 42.5641 2.78249 40.7825C4.56408 39.0009 6.98044 38 9.5 38H19V47.5C19 50.0196 17.9991 52.4359 16.2175 54.2175C14.4359 55.9991 12.0196 57 9.5 57C6.98044 57 4.56408 55.9991 2.78249 54.2175C1.00089 52.4359 0 50.0196 0 47.5Z" fill="#0ACF83"/>
            <path d="M19 0V19H28.5C31.0196 19 33.4359 17.9991 35.2175 16.2175C36.9991 14.4359 38 12.0196 38 9.5C38 6.98044 36.9991 4.56408 35.2175 2.78249C33.4359 1.00089 31.0196 0 28.5 0H19Z" fill="#FF7262"/>
            <path d="M0 9.5C0 12.0196 1.00089 14.4359 2.78249 16.2175C4.56408 17.9991 6.98044 19 9.5 19H19V0H9.5C6.98044 0 4.56408 1.00089 2.78249 2.78249C1.00089 4.56408 0 6.98044 0 9.5Z" fill="#F24E1E"/>
            <path d="M0 28.5C0 31.0196 1.00089 33.4359 2.78249 35.2175C4.56408 36.9991 6.98044 38 9.5 38H19V19H9.5C6.98044 19 4.56408 20.0009 2.78249 21.7825C1.00089 23.5641 0 25.9804 0 28.5Z" fill="#A259FF"/>
          </svg>
          Im Figma ansehen
        </a>

        <!-- Presentation Button -->
        <a
          v-if="project.presentationUrl"
          :href="project.presentationUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-2 px-5 py-2.5 bg-dark-900 text-white text-sm font-medium rounded-lg hover:bg-dark-700 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z" fill="white"/>
            <path d="M7 12H9V17H7V12ZM11 7H13V17H11V7ZM15 9H17V17H15V9Z" fill="white"/>
          </svg>
          Präsentation ansehen
        </a>
      </header>

      <!-- Challenge Section -->
      <section class="mb-16">
        <h2 class="text-3xl font-bold text-dark-900 mb-6">Challenge</h2>
        <p class="text-lg text-dark-600 leading-relaxed mb-8">
          {{ project.challenge.problem }}
        </p>

        <!-- Before Image -->
        <figure v-if="project.challenge.beforeImage" class="mb-10 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
          <img
            :src="project.challenge.beforeImage"
            alt="Ausgangssituation: Original ZEP Zeiterfassung"
            class="w-full h-auto block"
          />
          <figcaption class="px-4 py-3 text-sm text-dark-500 bg-gray-50">
            Ausgangssituation: Original ZEP Zeiterfassungsmaske vor dem Redesign
          </figcaption>
        </figure>

        <div class="grid md:grid-cols-2 gap-8">
          <!-- Stakeholders -->
          <div>
            <h3 class="text-xl font-semibold text-dark-900 mb-4">
              Stakeholder
            </h3>
            <ul class="space-y-2">
              <li
                v-for="stakeholder in project.challenge.stakeholders"
                :key="stakeholder"
                class="flex items-start"
              >
                <svg
                  class="w-5 h-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-dark-600">{{ stakeholder }}</span>
              </li>
            </ul>
          </div>

          <!-- Goals -->
          <div>
            <h3 class="text-xl font-semibold text-dark-900 mb-4">Ziele</h3>
            <ul class="space-y-2">
              <li
                v-for="goal in project.challenge.goals"
                :key="goal"
                class="flex items-start"
              >
                <svg
                  class="w-5 h-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-dark-600">{{ goal }}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <!-- Process Section -->
      <section class="mb-16 bg-gray-50 -mx-6 px-6 py-12 md:rounded-lg">
        <h2 class="text-3xl font-bold text-dark-900 mb-6">Process</h2>

        <div class="space-y-8">
          <!-- Methods -->
          <div>
            <h3 class="text-xl font-semibold text-dark-900 mb-4">Methoden</h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="method in project.process.methods"
                :key="method"
                class="px-4 py-2 bg-white text-dark-700 border border-gray-200 rounded-lg text-sm"
              >
                {{ method }}
              </span>
            </div>
          </div>

          <!-- Tools -->
          <div>
            <h3 class="text-xl font-semibold text-dark-900 mb-4">Tools</h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="tool in project.process.tools"
                :key="tool"
                class="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium"
              >
                {{ tool }}
              </span>
            </div>
          </div>

          <!-- Team & Duration -->
          <div class="grid md:grid-cols-2 gap-6">
            <div>
              <h3 class="text-xl font-semibold text-dark-900 mb-3">Team</h3>
              <p class="text-dark-600">{{ project.process.team }}</p>
            </div>
            <div>
              <h3 class="text-xl font-semibold text-dark-900 mb-3">Dauer</h3>
              <p class="text-dark-600">{{ project.process.duration }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Solution Section -->
      <section class="mb-16">
        <h2 class="text-3xl font-bold text-dark-900 mb-6">Solution</h2>
        <p class="text-lg text-dark-600 leading-relaxed mb-8">
          {{ project.solution.description }}
        </p>

        <!-- Key Features -->
        <div class="mb-8">
          <h3 class="text-xl font-semibold text-dark-900 mb-4">
            Key Features
          </h3>
          <ul class="space-y-3">
            <li
              v-for="feature in project.solution.keyFeatures"
              :key="feature"
              class="flex items-start"
            >
              <svg
                class="w-6 h-6 text-primary-500 mr-3 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span class="text-dark-700">{{ feature }}</span>
            </li>
          </ul>
        </div>

        <!-- Design Decisions -->
        <div>
          <h3 class="text-xl font-semibold text-dark-900 mb-4">
            Design-Entscheidungen
          </h3>
          <div class="space-y-4">
            <div
              v-for="decision in project.solution.designDecisions"
              :key="decision"
              class="p-4 bg-gray-50 rounded-lg border-l-4 border-primary-500"
            >
              <p class="text-dark-700">{{ decision }}</p>
            </div>
          </div>
        </div>

        <!-- Gallery -->
        <div v-if="project.solution.gallery?.length" class="mt-12">
          <h3 class="text-xl font-semibold text-dark-900 mb-6">Screens</h3>
          <div class="space-y-8">
            <figure
              v-for="(image, index) in project.solution.gallery"
              :key="index"
              class="rounded-xl overflow-hidden border border-gray-200 shadow-sm"
            >
              <div class="bg-gray-100 flex items-center justify-center min-h-64">
                <img
                  :src="image.src"
                  :alt="image.caption"
                  class="w-full h-auto block"
                  @error="($event.target as HTMLImageElement).style.display = 'none'"
                />
              </div>
              <figcaption class="px-4 py-3 text-sm text-dark-500 bg-gray-50">
                {{ image.caption }}
              </figcaption>
            </figure>
          </div>
        </div>

        <!-- Wireframes Gallery -->
        <div v-if="project.solution.wireframesGallery?.length" class="mt-16">
          <h3 class="text-xl font-semibold text-dark-900 mb-2">Wireframes</h3>
          <p class="text-sm text-dark-500 mb-6">{{ project.solution.wireframesGallery.length }} Screens</p>
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            <figure
              v-for="(image, index) in project.solution.wireframesGallery"
              :key="index"
              class="rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-gray-50"
            >
              <div class="bg-gray-100 flex items-center justify-center aspect-[9/16]">
                <img
                  :src="image.src"
                  :alt="image.caption"
                  class="w-full h-full object-contain"
                  @error="($event.target as HTMLImageElement).style.display = 'none'"
                />
              </div>
              <figcaption class="px-2 py-2 text-xs text-dark-500 leading-snug">
                {{ image.caption }}
              </figcaption>
            </figure>
          </div>
        </div>

        <!-- High-Fidelity Gallery -->
        <div v-if="project.solution.hifiGallery?.length" class="mt-16">
          <h3 class="text-xl font-semibold text-dark-900 mb-2">High-Fidelity Designs</h3>
          <p class="text-sm text-dark-500 mb-6">{{ project.solution.hifiGallery.length }} finale Screens</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <figure
              v-for="(image, index) in project.solution.hifiGallery"
              :key="index"
              class="rounded-xl overflow-hidden border border-gray-200 shadow-sm"
            >
              <div class="bg-gray-100 flex items-center justify-center aspect-[9/16]">
                <img
                  :src="image.src"
                  :alt="image.caption"
                  class="w-full h-full object-contain"
                  @error="($event.target as HTMLImageElement).style.display = 'none'"
                />
              </div>
              <figcaption class="px-4 py-3 text-sm text-dark-500 bg-gray-50">
                {{ image.caption }}
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <!-- Impact Section -->
      <section class="mb-16 bg-primary-50 -mx-6 px-6 py-12 md:rounded-lg">
        <h2 class="text-3xl font-bold text-dark-900 mb-6">Impact</h2>

        <!-- KPIs -->
        <div class="grid md:grid-cols-2 gap-4 mb-8">
          <div
            v-for="kpi in project.impact.kpis"
            :key="kpi"
            class="p-6 bg-white rounded-lg shadow-sm"
          >
            <div class="flex items-start">
              <svg
                class="w-8 h-8 text-primary-500 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
              </svg>
              <p class="text-dark-900 font-semibold">{{ kpi }}</p>
            </div>
          </div>
        </div>

        <!-- User Feedback -->
        <div v-if="project.impact.userFeedback" class="mb-8">
          <h3 class="text-xl font-semibold text-dark-900 mb-4">
            User Feedback
          </h3>
          <blockquote
            class="p-6 bg-white rounded-lg border-l-4 border-primary-500 italic text-dark-700"
          >
            {{ project.impact.userFeedback }}
          </blockquote>
        </div>

        <!-- Business Impact -->
        <div class="mb-8">
          <h3 class="text-xl font-semibold text-dark-900 mb-4">
            Business Impact
          </h3>
          <p class="text-dark-700 leading-relaxed">
            {{ project.impact.businessImpact }}
          </p>
        </div>

        <!-- Lessons Learned -->
        <div>
          <h3 class="text-xl font-semibold text-dark-900 mb-4">
            Lessons Learned
          </h3>
          <ul class="space-y-3">
            <li
              v-for="lesson in project.impact.lessonsLearned"
              :key="lesson"
              class="flex items-start"
            >
              <svg
                class="w-6 h-6 text-primary-500 mr-3 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
              <span class="text-dark-700">{{ lesson }}</span>
            </li>
          </ul>
        </div>
      </section>

      <!-- Project Navigation -->
      <nav
        class="flex justify-between items-center pt-12 border-t border-gray-200"
      >
        <NuxtLink
          v-if="prevProject"
          :to="`/portfolio/${prevProject.slug}`"
          class="group flex items-center text-dark-600 hover:text-primary-500 transition-colors"
        >
          <svg
            class="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <div>
            <p class="text-sm text-dark-500">Vorheriges Projekt</p>
            <p class="font-semibold">{{ prevProject.title }}</p>
          </div>
        </NuxtLink>
        <div v-else />

        <NuxtLink
          v-if="nextProject"
          :to="`/portfolio/${nextProject.slug}`"
          class="group flex items-center text-dark-600 hover:text-primary-500 transition-colors text-right"
        >
          <div>
            <p class="text-sm text-dark-500">Nächstes Projekt</p>
            <p class="font-semibold">{{ nextProject.title }}</p>
          </div>
          <svg
            class="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </NuxtLink>
        <div v-else />
      </nav>
    </div>
  </div>
</template>
